import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicBySlug, getRelatedComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  if (!comic) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
          <p className="mt-4 text-foreground-500 text-lg">漫画未找到</p>
          <Link
            to="/"
            className="mt-4 inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.id === comic.category);
  const relatedComics = getRelatedComics(comic.id, 4);
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const chapterList = Array.from({ length: comic.chapters }, (_, i) => ({
    num: sortOrder === 'desc' ? comic.chapters - i : i + 1,
    title: `第${sortOrder === 'desc' ? comic.chapters - i : i + 1}话`,
    date: new Date(new Date(comic.updatedAt).getTime() - (comic.chapters - 1 - i) * 86400000)
      .toISOString().split('T')[0],
  }));

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ComicStory',
      name: comic.title,
      author: { '@type': 'Person', name: comic.author },
      description: comic.description,
      url: `http://nheap.cn/comic/${comic.slug}`,
      image: comic.coverImage,
      genre: category?.name || '',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://nheap.cn/' },
        { '@type': 'ListItem', position: 2, name: category?.name || '全部', item: `http://nheap.cn/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title={`${comic.title} - ${comic.author} - 好看的韩国漫画 - 韩漫屋`}
        description={`韩漫屋免费阅读《${comic.title}》，作者：${comic.author}。${comic.description.slice(0, 120)}。韩漫免费阅读，尽在韩漫屋。`}
        keywords={`好看的韩国漫画, 韩漫免费阅读, 韩漫网址, 韩漫屋, ${comic.title}, ${comic.author}`}
        canonicalUrl={`http://nheap.cn/comic/${comic.slug}`}
        ogTitle={`${comic.title} - 韩漫屋`}
        ogDescription={comic.description.slice(0, 160)}
        jsonLd={articleJsonLd}
      />

      <main className="pt-16 min-h-screen">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '全部', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px]">
              <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 好看的韩国漫画`}
                  title={`${comic.title} - ${comic.author}`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-700 text-background-100'
                  }`}>
                    {comic.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-2">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-500">
                  <i className="ri-user-line mr-1"></i>{comic.author}
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-folder-line mr-1"></i>{category?.name}
                </span>
                <span className="flex items-center gap-1 text-sm text-accent-600">
                  <i className="ri-star-fill"></i>
                  <strong>{comic.rating}</strong>
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>{formatViews(comic.views)} 阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6 bg-background-100 rounded-xl p-4">
                {comic.description}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="#"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  <i className="ri-play-circle-line"></i>
                  开始阅读
                </Link>
                <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-heart-line"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                </button>
              </div>
            </div>
          </article>

          <section className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg text-foreground-900">
                章节目录
                <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters} 话</span>
              </h2>
              <button
                onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className={sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}></i>
                {sortOrder === 'desc' ? '最新在前' : '最早在前'}
              </button>
            </div>
            <div className="bg-background-100 rounded-xl p-1 max-h-[500px] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                {chapterList.map((ch) => (
                  <Link
                    key={ch.num}
                    to="#"
                    className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-background-200 transition-colors group"
                  >
                    <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                <h3 className="font-heading font-bold text-lg text-foreground-900">相关推荐</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop>
                {relatedComics.map((rc) => {
                  const cat = categories.find((c) => c.id === rc.category);
                  return (
                    <ComicCard
                      key={rc.id}
                      id={rc.id}
                      slug={rc.slug}
                      title={rc.title}
                      author={rc.author}
                      category={rc.category}
                      status={rc.status}
                      rating={rc.rating}
                      views={rc.views}
                      chapters={rc.chapters}
                      coverImage={rc.coverImage}
                      categoryName={cat?.name}
                    />
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}